import React, { useEffect, useRef, useState } from 'react';

/**
 * Reusable Map component that dynamically loads Leaflet from CDN.
 * Provides accessible controls, geolocation with permissions explanation,
 * and robust loading/error messaging.
 */
const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
const CSS_ID = 'leaflet-css-cdn';
const JS_ID = 'leaflet-js-cdn';
const DEFAULT_LAT = 38.2527;
const DEFAULT_LNG = -85.7585;

function loadLeaflet() {
  return new Promise((resolve, reject) => {
    if (window.L) {
      resolve(window.L);
      return;
    }

    const timeout = setTimeout(() => {
      reject(new Error('Map load timed out. Please enter coordinates manually or describe the location below.'));
    }, 10000);

    const handleSuccess = (L) => {
      clearTimeout(timeout);
      resolve(L);
    };

    const handleError = () => {
      clearTimeout(timeout);
      reject(new Error('Failed to load map libraries. Please enter coordinates manually or describe the location below.'));
    };

    if (!document.getElementById(CSS_ID)) {
      const link = document.createElement('link');
      link.id = CSS_ID;
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      link.crossOrigin = '';
      document.head.appendChild(link);
    }

    const existing = document.getElementById(JS_ID);
    if (existing) {
      if (window.L) {
        handleSuccess(window.L);
        return;
      }
      existing.addEventListener('load', () => handleSuccess(window.L), { once: true });
      existing.addEventListener('error', handleError, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = JS_ID;
    script.src = LEAFLET_JS;
    script.crossOrigin = '';
    script.onload = () => handleSuccess(window.L);
    script.onerror = handleError;
    document.body.appendChild(script);
  });
}

const MapComponent = ({
  mode = 'select',
  lat = DEFAULT_LAT,
  lng = DEFAULT_LNG,
  zoom = 11,
  markers = [],
  onLocationSelect,
  showGeolocate = true,
}) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerInstance = useRef(null);
  const onLocationSelectRef = useRef(onLocationSelect);
  const [leafletLoaded, setLeafletLoaded] = useState(!!window.L);
  const [loadError, setLoadError] = useState(null);
  const [geoStatus, setGeoStatus] = useState(null); // null | locating | error message | success message
  const [geoBusy, setGeoBusy] = useState(false);
  const [showPermissionExplanation, setShowPermissionExplanation] = useState(false);
  // Local controlled inputs for manual coordinate fallback (map CDN failure / keyboard users)
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');
  const [manualCoordError, setManualCoordError] = useState(null);

  useEffect(() => {
    onLocationSelectRef.current = onLocationSelect;
  }, [onLocationSelect]);

  useEffect(() => {
    let cancelled = false;

    loadLeaflet()
      .then(() => {
        if (!cancelled) {
          setLeafletLoaded(true);
          setLoadError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(
            err?.message ||
              'Map failed to load. You can still enter coordinates manually or describe the location in the text fields.'
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const placeMarker = (clickedLat, clickedLng, zoomTo = 15) => {
    if (onLocationSelectRef.current) {
      onLocationSelectRef.current(clickedLat, clickedLng);
    }

    if (!mapInstance.current || !window.L) return;
    const L = window.L;

    if (markerInstance.current) {
      markerInstance.current.setLatLng([clickedLat, clickedLng]);
    } else {
      markerInstance.current = L.marker([clickedLat, clickedLng]).addTo(mapInstance.current);
    }
    mapInstance.current.setView([clickedLat, clickedLng], zoomTo);
  };

  useEffect(() => {
    if (!leafletLoaded || !mapRef.current || !window.L) return;

    const L = window.L;

    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
      iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
      shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    });

    if (!mapInstance.current) {
      mapInstance.current = L.map(mapRef.current).setView([lat, lng], zoom);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(mapInstance.current);

      if (mode === 'select') {
        const isCustomLocation = lat !== DEFAULT_LAT || lng !== DEFAULT_LNG;
        if (isCustomLocation && lat && lng) {
          markerInstance.current = L.marker([lat, lng]).addTo(mapInstance.current);
          mapInstance.current.setView([lat, lng], 14);
        }

        mapInstance.current.on('click', (e) => {
          const { lat: clickedLat, lng: clickedLng } = e.latlng;
          placeMarker(clickedLat, clickedLng, mapInstance.current.getZoom());
        });
      }
    }

    if (mode === 'view' && mapInstance.current) {
      if (!mapInstance.current._markerGroup) {
        mapInstance.current._markerGroup = L.layerGroup().addTo(mapInstance.current);
      } else {
        mapInstance.current._markerGroup.clearLayers();
      }

      const bounds = [];
      markers.forEach((m) => {
        if (m.lat != null && m.lng != null) {
          const marker = L.marker([m.lat, m.lng]);
          if (m.popupText) {
            marker.bindPopup(m.popupText);
          }
          marker.addTo(mapInstance.current._markerGroup);
          bounds.push([m.lat, m.lng]);
        }
      });

      if (bounds.length > 0) {
        try {
          mapInstance.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
        } catch {
          // ignore fitBounds edge cases
        }
      }
    }
  }, [leafletLoaded, mode, markers, lat, lng, zoom]);

  useEffect(() => {
    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
        markerInstance.current = null;
      }
    };
  }, []);

  const triggerGeolocation = () => {
    setShowPermissionExplanation(false);
    if (!navigator.geolocation) {
      setGeoStatus('Location is not supported on this device/browser.');
      return;
    }

    setGeoBusy(true);
    setGeoStatus('locating');

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        placeMarker(latitude, longitude, 16);
        setGeoStatus('success');
        setGeoBusy(false);
      },
      (err) => {
        let msg = 'Could not get your location. You can still pin the map manually.';
        if (err.code === 1) {
          msg = 'Location permission denied. Please pin the map manually or enable location access in settings.';
        } else if (err.code === 2) {
          msg = 'Location unavailable. Please pin the map manually instead.';
        } else if (err.code === 3) {
          msg = 'Location request timed out. Please try again or pin the map manually.';
        }
        setGeoStatus(msg);
        setGeoBusy(false);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  };

  const handleGeolocateClick = () => {
    // Show permission explanation before requesting access
    setShowPermissionExplanation(true);
  };

  const applyManualCoordinates = () => {
    setManualCoordError(null);
    const latNum = Number(manualLat);
    const lngNum = Number(manualLng);
    if (manualLat === '' || manualLng === '') {
      setManualCoordError('Enter both latitude and longitude.');
      return;
    }
    if (Number.isNaN(latNum) || latNum < -90 || latNum > 90) {
      setManualCoordError('Latitude must be a number between -90 and 90.');
      return;
    }
    if (Number.isNaN(lngNum) || lngNum < -180 || lngNum > 180) {
      setManualCoordError('Longitude must be a number between -180 and 180.');
      return;
    }
    placeMarker(latNum, lngNum, 15);
    setGeoStatus('success');
  };

  return (
    <div className="space-y-2">
      {mode === 'select' && showGeolocate && (
        <div className="bg-charcoal-card/30 p-3 rounded-lg border border-white/5 space-y-2">
          <p className="text-xs text-slate-400">
            Sharing your location coordinates helps outreach volunteers find you much faster. Providing location info is completely optional.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <button
              type="button"
              onClick={handleGeolocateClick}
              disabled={geoBusy}
              className="inline-flex items-center justify-center gap-2 bg-accent-cyan/15 hover:bg-accent-cyan/25 text-accent-cyan border border-accent-cyan/40 font-bold text-xs uppercase tracking-wider px-4 py-2 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {geoBusy ? 'Locating…' : 'Use my location'}
            </button>
            {geoStatus && geoStatus !== 'locating' && geoStatus !== 'success' && (
              <p className="text-xs text-urgent-orange" role="status">
                ⚠️ {geoStatus}
              </p>
            )}
            {geoStatus === 'locating' && (
              <p className="text-xs text-slate-400 animate-pulse" role="status">
                Finding your position…
              </p>
            )}
            {geoStatus === 'success' && (
              <p className="text-xs text-hope-green" role="status">
                ✓ Location coordinates obtained successfully.
              </p>
            )}
          </div>

          {showPermissionExplanation && (
            <div className="bg-charcoal-dark border border-accent-cyan/20 p-3 rounded mt-2 text-xs space-y-2">
              <p className="text-slate-300">
                This app will request permission to access your device's location. This location is used solely to coordinate delivery of outreach supplies.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={triggerGeolocation}
                  className="bg-accent-cyan text-charcoal-dark font-bold px-3 py-1 rounded hover:bg-accent-cyan/90 text-2xs uppercase tracking-wider"
                >
                  Allow & Locate
                </button>
                <button
                  type="button"
                  onClick={() => setShowPermissionExplanation(false)}
                  className="bg-white/10 hover:bg-white/15 text-white font-bold px-3 py-1 rounded text-2xs uppercase tracking-wider"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      <div className="text-xs text-slate-400 space-y-1">
        <p><strong>Map Navigation Instructions:</strong></p>
        <ul className="list-disc list-inside space-y-1 pl-1 text-[11px]">
          <li><strong>Mouse Users:</strong> Click anywhere on the map to place a pin at that location.</li>
          <li><strong>Keyboard/Screen-Reader Fallback:</strong> If you cannot use the map, please skip it. You can write your nearest intersection, address, or landmarks in the text fields below.</li>
        </ul>
      </div>

      <div
        className="relative w-full h-[250px] sm:h-[300px] md:h-[350px] rounded-lg border border-white/10 overflow-hidden shadow-inner bg-charcoal-card"
        role="application"
        aria-label={mode === 'select' ? 'Map: click to pin a location' : 'Map of reported locations'}
      >
        {!leafletLoaded && !loadError && (
          <div className="absolute inset-0 bg-charcoal-card flex items-center justify-center z-10 text-slate-400 font-medium text-sm">
            Loading interactive map...
          </div>
        )}
        {loadError && (
          <div className="absolute inset-0 bg-charcoal-card flex items-center justify-center z-10 text-slate-400 font-medium text-sm px-4 text-center">
            {loadError}
          </div>
        )}
        <div ref={mapRef} className="w-full h-full z-0" />
      </div>

      {mode === 'select' && (
        <div className="bg-charcoal-card/40 p-4 rounded-lg border border-white/5 space-y-3 mt-2">
          <p className="text-xs text-slate-300 font-bold uppercase tracking-wider">
            {loadError ? '📍 Manual coordinates (map unavailable)' : '📍 Optional manual coordinates'}
          </p>
          <p className="text-[11px] text-slate-400 leading-relaxed">
            {loadError
              ? 'The interactive map could not load. Paste coordinates from Google Maps or another app (for example 38.2527, -85.7585), or skip this and describe the location in the text fields.'
              : 'Keyboard and screen-reader friendly alternative to pin placement. Leave blank if you only want a written location description.'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="manual-latitude" className="block text-[11px] text-slate-400 font-semibold mb-1">
                Latitude (-90 to 90)
              </label>
              <input
                id="manual-latitude"
                type="number"
                step="any"
                min="-90"
                max="90"
                inputMode="decimal"
                value={manualLat}
                onChange={(e) => {
                  setManualLat(e.target.value);
                  setManualCoordError(null);
                }}
                className="w-full bg-charcoal-dark border border-white/10 rounded px-3 py-2 text-xs text-white placeholder-slate-600 focus:ring-2 focus:ring-accent-cyan"
                placeholder="e.g. 38.2527"
              />
            </div>
            <div>
              <label htmlFor="manual-longitude" className="block text-[11px] text-slate-400 font-semibold mb-1">
                Longitude (-180 to 180)
              </label>
              <input
                id="manual-longitude"
                type="number"
                step="any"
                min="-180"
                max="180"
                inputMode="decimal"
                value={manualLng}
                onChange={(e) => {
                  setManualLng(e.target.value);
                  setManualCoordError(null);
                }}
                className="w-full bg-charcoal-dark border border-white/10 rounded px-3 py-2 text-xs text-white placeholder-slate-600 focus:ring-2 focus:ring-accent-cyan"
                placeholder="e.g. -85.7585"
              />
            </div>
          </div>
          {manualCoordError && (
            <p className="text-xs text-urgent-orange" role="alert">
              {manualCoordError}
            </p>
          )}
          <button
            type="button"
            onClick={applyManualCoordinates}
            className="inline-flex items-center justify-center bg-accent-cyan/15 hover:bg-accent-cyan/25 text-accent-cyan border border-accent-cyan/40 font-bold text-xs uppercase tracking-wider px-4 py-2 rounded transition-colors"
          >
            Apply coordinates
          </button>
        </div>
      )}
    </div>
  );
};

export default MapComponent;
